import { useCallback, useEffect, useMemo, useState } from 'react';
import Cookie from 'js-cookie';
import {
    getEventCookieName,
    setEventCookie,
    useCommercialMessage,
} from '@pegasus/commercial-messages';
import { useConfig } from '@browsing/config';
import { getQueryParams } from '@browsing/utils';

import { getCookie } from '@client/helpers/cookie';

const useCustomCommercialMessage = () => {
    const config = useConfig();

    const event = useCommercialMessage();

    const [isShown, setShown] = useState(false);

    const isEnabled = useMemo(() => {
        const featureCookie = getCookie('zl_commercial_messages');

        if (featureCookie === 'false') {
            return false;
        }

        const isFeatureEnabled = config.isEnabled('commercialMessages');
        const isFeatureCookieEnabled = featureCookie === 'true';

        return isFeatureEnabled || isFeatureCookieEnabled;
    }, [config]);

    const onClose = useCallback(() => {
        setShown(false);
    }, []);

    const onRedeem = useCallback(
        (name: string, { voucherCode }: { voucherCode: string }) => {
            const redeemPath = event?.redeemRedirectionPath ?? '/event';

            window.location.assign(`${redeemPath}?z_coup=${voucherCode}&z_message=${name}`);
        },
        [event],
    );

    useEffect(() => {
        let isMounted = true;
        let isCouponDismissed = false;

        const { z_coup: coupon, z_message: message } = getQueryParams();

        if (!message && coupon && event) {
            isCouponDismissed = true;

            if (event.voucherCode !== coupon) {
                /**
                 * When the coupon in the query params exist but does not match current commercial event
                 * hide it for the current session so that the customer will not lose opportunity to use it.
                 */
                Cookie.set(getEventCookieName(event.voucherCode), 'true');
            } else {
                /**
                 * When the coupon in the query params exist and matches current commercial event
                 * hide it till the end date of the event, because the customer already applied the coupon by other source.
                 */
                onClose();

                setEventCookie(event.voucherCode, event.endDate, event.reusableCode);
            }
        }

        const triggerEventMessage = () => {
            if (isMounted && !isCouponDismissed && !!event) {
                setShown(true);
            }
        };

        if ('requestIdleCallback' in window) {
            // @ts-ignore
            window.requestIdleCallback(triggerEventMessage);
        } else {
            setTimeout(triggerEventMessage, 5000);
        }

        return () => {
            isMounted = false;
        };
    }, [event, onClose]);

    const isModal = event?.voucherType === 'MODAL';

    return {
        event,
        isShown,
        isEnabled: isEnabled && !!event,
        isModal,
        onClose,
        onRedeem,
    };
};

export default useCustomCommercialMessage;
